import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  CheckoutProvider,
  PaymentElement,
  useCheckout,
} from '@stripe/react-stripe-js/checkout';
import { useScripts } from '../hooks/useScripts';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getDomain } from '../utils/getDomain';
import { stripePromise } from '../stripe';
import { CREATE_CHECKOUT_SESSION_URL } from '../config';
import { PLAN_CONFIG } from '../constants/plans';
import { getCheckoutV2Locale } from '../constants/checkoutV2Locales';
import { PricingTable } from '../components/PricingTable';
import { setClarityUserId } from '../utils/clarity';

// Signup page pricing section uses shared PricingTable
function SignupPricing({ onPlanSelect, selectedPlanKey }) {
  const domain = getDomain();
  const siteName = domain.split('.')[0].toUpperCase();
  return (
    <div className="pricing-table animatedParent">
      <div className="container">
        <PricingTable
          title={`Signup to ${siteName} and enjoy your privacy`}
          variant="signup"
          onPlanSelect={onPlanSelect}
          selectedPlanKey={selectedPlanKey}
        />
      </div>
    </div>
  );
}

// Stripe checkout form component - used inside CheckoutProvider
function StripeCheckoutForm({ onSuccess, onError, disabled, formData }) {
  const checkoutResult = useCheckout();

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (checkoutResult.type !== 'success' || !checkoutResult.checkout) {
      return;
    }
    // Store in sessionStorage before confirm() in case Stripe redirects (e.g. 3DS)
    if (formData?.email && formData?.password) {
      sessionStorage.setItem('stripe_signup_pending', JSON.stringify(formData));
    }
    const { confirm } = checkoutResult.checkout;
    try {
      const result = await confirm();
      if (result && result.error) {
        sessionStorage.removeItem('stripe_signup_pending');
        onError?.(result.error.message || 'Payment failed');
      } else {
        sessionStorage.removeItem('stripe_signup_pending');
        onSuccess?.();
      }
    } catch (err) {
      sessionStorage.removeItem('stripe_signup_pending');
      onError?.(err.message || 'An error occurred');
    }
  };

  if (checkoutResult.type === 'loading') {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#4a5568' }}>
        Loading payment form...
      </div>
    );
  }
  if (checkoutResult.type === 'error') {
    return (
      <div style={{ padding: '20px', color: '#e53e3e' }}>
        {checkoutResult.error?.message || 'Failed to load payment form'}
      </div>
    );
  }

  return (
    <>
      <PaymentElement />
      <button
        type="button"
        className="btn"
        disabled={disabled}
        onClick={handleSubmit}
        style={{
          width: '100%',
          padding: '12px 24px',
          marginTop: '16px',
          fontSize: '16px',
          fontWeight: 600,
          backgroundColor: disabled ? '#cbd5e0' : '#2563eb',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        Pay Now
      </button>
    </>
  );
}

function Signup() {
  const navigate = useNavigate();
  const domain = getDomain();
  const [selectedPlanKey, setSelectedPlanKey] = useState('vpn_30_day');
  const [clientSecret, setClientSecret] = useState(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isActiveSubscriptionError, setIsActiveSubscriptionError] = useState(false);
  const [signupError, setSignupError] = useState(null);
  const [formData, setFormData] = useState({ email: '', password: '' });

  useDocumentTitle('Sign Up');
  useScripts();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (plan && PLAN_CONFIG[plan]) {
      setSelectedPlanKey(plan);
    }
  }, []);

  const handlePlanSelect = (planKey) => {
    setSelectedPlanKey(planKey);
    setClientSecret(null);
    setPaymentError(null);
    setIsActiveSubscriptionError(false);
  };

  useEffect(() => {
    document.body.classList.add('signup');
    return () => document.body.classList.remove('signup');
  }, []);

  // Handle return from Stripe redirect (e.g. 3DS) — send user to success page so it can submit session_id to vpn-signup
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    const pending = sessionStorage.getItem('stripe_signup_pending');
    if (!pending) return;

    try {
      const { email, password } = JSON.parse(pending);
      sessionStorage.removeItem('stripe_signup_pending');
      setFormData({ email, password });
      navigate(`/success?session_id=${sessionId}`, { replace: true });
    } catch {
      sessionStorage.removeItem('stripe_signup_pending');
    }
  }, [navigate]);

  const amount = PLAN_CONFIG[selectedPlanKey] || PLAN_CONFIG.vpn_30_day;

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    const emailInput = e.target.querySelector('input[type="email"]');
    const passwordInput = e.target.querySelector('input[type="password"]');
    const confirmInput = e.target.querySelectorAll('input[type="password"]')[1];

    const email = emailInput?.value?.trim();
    const password = passwordInput?.value;
    const confirmPassword = confirmInput?.value;

    if (!email || !password || !confirmPassword) {
      setPaymentError('Please fill in all fields');
      return;
    }
    if (password !== confirmPassword) {
      setPaymentError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setPaymentError('Password must be at least 6 characters');
      return;
    }

    setClarityUserId(email);
    setPaymentError(null);
    setIsActiveSubscriptionError(false);
    setFormData({ email, password });
    setIsLoadingCheckout(true);

    try {
      const returnUrl = `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`;
      const urlParams = new URLSearchParams(window.location.search);
      const checkoutLocale = getCheckoutV2Locale(urlParams);
      const checkoutBody = {
        customer_email: email,
        password,
        plan: selectedPlanKey,
        product_name: `VPN Subscription - ${amount.label}`,
        price_lookup_key: selectedPlanKey,
        return_url: returnUrl,
        origin: window.location.origin,
      };
      if (checkoutLocale !== 'en') {
        checkoutBody.locale = checkoutLocale;
      }

      const response = await fetch(CREATE_CHECKOUT_SESSION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutBody),
      });

      const text = await response.text();
      if (!text) {
        setPaymentError('Empty response from server. Check function logs.');
        setIsLoadingCheckout(false);
        return;
      }
      let data = {};
      try {
        data = JSON.parse(text);
      } catch {
        setPaymentError('Invalid response from server');
        setIsLoadingCheckout(false);
        return;
      }
      const body = data.body ?? data;
      const statusCode = data.statusCode ?? response.status;

      if (statusCode === 409) {
        setIsActiveSubscriptionError(true);
        setPaymentError(
          body?.error || 'This email already has an active subscription.'
        );
        setIsLoadingCheckout(false);
        return;
      }

      if (body?.error) {
        setPaymentError(body.error);
        setIsLoadingCheckout(false);
        return;
      }

      const secret = body.clientSecret;
      if (!secret) {
        setPaymentError('Invalid response from server (missing clientSecret)');
        setIsLoadingCheckout(false);
        return;
      }

      setClientSecret(secret);
      if (body.sessionId) setCheckoutSessionId(body.sessionId);
    } catch (err) {
      setPaymentError(
        err.message || 'Failed to initialize checkout. Please try again.'
      );
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentError(null);
    const sessionId = checkoutSessionId;
    setClientSecret(null);
    setCheckoutSessionId(null);
    if (sessionId) {
      navigate(`/success?session_id=${sessionId}`, { replace: true });
    } else {
      setSignupError('Missing session. Please go to the success page from your payment confirmation email or try again.');
    }
  };

  return (
    <>
      <SignupPricing onPlanSelect={handlePlanSelect} selectedPlanKey={selectedPlanKey} />

      <div className="bg-form">
        <div className="container">
          <p className="text-center">
            Join us today and start protecting your online privacy.
          </p>
        </div>
      </div>

      <div
        id="signup-form"
        className="form-contact"
        style={{ zIndex: 'auto', marginBottom: '40px' }}
      >
        <h3>Subscribe Below</h3>

        <form onSubmit={handleProceedToPayment}>
            {clientSecret ? (
              <div
                style={{
                  padding: '12px 16px',
                  marginBottom: '20px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  color: '#4a5568',
                }}
              >
                <span style={{ fontWeight: 600, color: '#2d3748' }}>Account:</span>{' '}
                {formData.email}
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Enter your email address</label>
                  <input type="email" required />
                </div>
                <div className="form-group">
                  <label>Choose a password</label>
                  <input type="password" required minLength={6} />
                </div>
                <div className="form-group">
                  <label>Confirm password</label>
                  <input type="password" required minLength={6} />
                </div>
              </>
            )}

            <div
              className="payment-summary"
              style={{
                marginTop: '30px',
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: '#f7fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            >
              <h4
                style={{
                  margin: '0 0 15px 0',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#2d3748',
                }}
              >
                Payment Summary
              </h4>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '10px',
                }}
              >
                <span style={{ color: '#4a5568', fontSize: '15px' }}>
                  {amount.label}
                </span>
                <span
                  style={{
                    color: '#2d3748',
                    fontSize: '15px',
                    fontWeight: 500,
                  }}
                >
                  ${amount.display}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '15px',
                  borderTop: '2px solid #e2e8f0',
                  marginTop: '10px',
                }}
              >
                <span
                  style={{
                    color: '#2d3748',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  Total
                </span>
                <span
                  style={{
                    color: '#2d3748',
                    fontSize: '18px',
                    fontWeight: 700,
                  }}
                >
                  ${amount.display}
                </span>
              </div>
            </div>

            {clientSecret ? (
              <CheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret,
                  elementsOptions: {
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#2563eb',
                        borderRadius: '6px',
                      },
                    },
                  },
                }}
              >
                {(paymentError || signupError) && (
                  <p
                    style={{
                      padding: '12px',
                      marginBottom: '16px',
                      backgroundColor: isActiveSubscriptionError ? '#fef3c7' : '#fed7d7',
                      color: isActiveSubscriptionError ? '#92400e' : '#c53030',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  >
                    {paymentError || signupError}
                    {isActiveSubscriptionError && (
                      <span style={{ display: 'block', marginTop: '10px' }}>
                        <Link to="/login" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>
                          Log in instead →
                        </Link>
                      </span>
                    )}
                  </p>
                )}
                <StripeCheckoutForm
                  onSuccess={handlePaymentSuccess}
                  onError={setPaymentError}
                  disabled={false}
                  formData={formData}
                />
                <button
                  type="button"
                  onClick={() => {
                    setClientSecret(null);
                    setPaymentError(null);
                    setIsActiveSubscriptionError(false);
                    setSignupError(null);
                  }}
                  style={{
                    marginTop: '12px',
                    width: '100%',
                    padding: '8px',
                    background: 'transparent',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#4a5568',
                  }}
                >
                  Back to form
                </button>
              </CheckoutProvider>
            ) : null}

            {!clientSecret && (
              <div className="form-group text-center" style={{ marginBottom: '20px' }}>
                <button
                  className="btn"
                  type="submit"
                  disabled={isLoadingCheckout}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    fontSize: '16px',
                    fontWeight: 600,
                    backgroundColor: isLoadingCheckout ? '#cbd5e0' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoadingCheckout ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isLoadingCheckout
                    ? 'Loading...'
                    : `Pay $${amount.display} USD`}
                </button>
                {paymentError && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: isActiveSubscriptionError ? '#fef3c7' : '#fed7d7',
                      color: isActiveSubscriptionError ? '#92400e' : '#c53030',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                  >
                    {paymentError}
                    {isActiveSubscriptionError && (
                      <div style={{ marginTop: '10px' }}>
                        <Link
                          to="/login"
                          style={{
                            color: '#2563eb',
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          Log in instead →
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div
              className="form-group text-center"
              style={{
                fontSize: '14px',
                color: '#8198a6',
                marginBottom: '20px',
                marginTop: clientSecret ? '20px' : 0,
              }}
            >
              <p style={{ margin: 0 }}>
                By submitting this form, you agree to our{' '}
                <a href="/tos" style={{ color: '#2563eb' }}>
                  Terms of Service
                </a>
                .
              </p>
            </div>
          </form>
      </div>

      <div style={{ marginTop: '80px', marginBottom: '40px' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <img
            src="/images/logo.svg"
            alt="VPN Logo"
            style={{ maxWidth: '200px', height: 'auto', marginBottom: '20px' }}
          />
          <h2
            style={{
              marginBottom: '0',
              fontSize: '42px',
              fontWeight: 300,
              color: '#3a3a3a',
            }}
          >
            See what you get with your subscription
          </h2>
        </div>
      </div>

      <div
        className="signup-footer"
        style={{
          padding: '80px 0',
          backgroundColor: '#2563eb',
          position: 'relative',
        }}
      >
        <div
          className="container"
          style={{
            position: 'relative',
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '0 40px',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '150px',
            }}
          >
              <div style={{ color: 'white', fontSize: '20px' }}>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No complicated set-up
                  </strong>
                  Our proprietary software makes set-up a snap on any of your
                  devices.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No limits
                  </strong>
                  We don't restrict use or track your bandwith use. Surf, upload
                  and download all you want.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    100% anonymous sign-up
                  </strong>
                  We never have to know your name or any identifiable information.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No logs
                  </strong>
                  We don't keep logs, so we don't know what you do with our
                  service.
                </div>
              </div>
              <div style={{ color: 'white', fontSize: '20px' }}>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No borders
                  </strong>
                  Access any content around the world, from anywhere in the
                  world.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    Three simultaneous connections
                  </strong>
                  Use up to three devices at the same time with your account.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    P2P friendly
                  </strong>
                  We don't restrict any usage.
                  <br />
                  You can Bittorent at will.
                </div>
                <div style={{ marginBottom: '50px' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333333',
                    }}
                  >
                    No kidding
                  </strong>
                  Unlimited speeds. Unlimited server switches.
                </div>
              </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Signup;
